import { collection, addDoc, getDoc, onSnapshot } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";

/**
 * Text to Speech Utility
 * 
 * This module handles text-to-speech generation by writing to Firestore and reading back
 * the generated audio URL. It uses an audioConversions collection where a backend service
 * processes the TTS request and updates the document with the audio file URL.
 * 
 * Workflow:
 * 1. Write document to 'audioConversions' collection with text and TTS parameters
 * 2. Wait for backend service to process and generate audio file
 * 3. Check document for 'audioPath' field, or use default 'audio_conversions/{docId}.mp3'
 * 4. Return the audio file download URL from Storage
 */

/**
 * Generate speech audio from text using Firestore-based TTS service
 * @param {string} text - The text to convert to speech
 * @param {string} languageCode - Language code (e.g., 'kn', 'en', 'hi')
 * @param {string} ssmlGender - Voice gender ('MALE', 'FEMALE', 'NEUTRAL')
 * @param {Object} db - Firestore instance
 * @param {Object} storage - Firebase Storage instance
 * @param {number} maxRetries - Maximum number of retry attempts (default: 5)
 * @param {number} retryDelay - Delay between retries in milliseconds (default: 2000)
 * @returns {Promise<string>} - The audio file URL
 * @throws {Error} - If TTS generation fails or times out
 */
export async function generateSpeechAudio(text, languageCode, ssmlGender, db, storage, maxRetries = 5, retryDelay = 2000) {
  if (!text || typeof text !== 'string') {
    throw new Error('Invalid text provided for TTS generation');
  }

  const trimmedText = text.trim();
  if (!trimmedText) {
    throw new Error('Empty text provided for TTS generation');
  }

  if (!languageCode) {
    throw new Error('Language code is required for TTS generation');
  }

  try {
    // Step 1: Create document in audioConversions collection
    const docRef = await createAudioConversionDocument(trimmedText, languageCode, ssmlGender, db);
    
    // Step 2: Wait for audio to be generated with retries
    const audioUrl = await waitForAudioGenerationWithRetries(docRef, db, storage, maxRetries, retryDelay);
    if (!audioUrl) {
      throw new Error('TTS service did not provide an audio URL after multiple attempts');
    }
    
    return audioUrl;
    
  } catch (error) {
    throw new Error(`TTS generation failed: ${error.message}`);
  }
}

/**
 * Generate speech audio for multiple texts in batch
 * @param {Array<{text: string, languageCode: string, ssmlGender: string}>} textItems - Array of TTS requests
 * @param {Object} db - Firestore instance
 * @param {Object} storage - Firebase Storage instance
 * @param {number} maxRetries - Maximum number of retry attempts per text
 * @param {number} retryDelay - Delay between retries in milliseconds
 * @returns {Promise<Array<string>>} - Array of audio file URLs
 * @throws {Error} - If batch TTS generation fails
 */
export async function generateBatchSpeechAudio(textItems, db, storage, maxRetries = 5, retryDelay = 2000) {
  if (!Array.isArray(textItems)) {
    throw new Error('Input must be an array of TTS requests');
  }

  if (textItems.length === 0) {
    return [];
  }

  try {
    // Create TTS promises for all items
    const ttsPromises = textItems.map(async (item, index) => { // eslint-disable-line no-unused-vars
      const { text, languageCode, ssmlGender } = item;
      
      if (!text || !text.trim()) {
        return '';
      }
      
      try {
        return await generateSpeechAudio(text, languageCode, ssmlGender, db, storage, maxRetries, retryDelay);
      } catch (error) {

        return `[TTS Error: ${text}]`;
      }
    });
    
    // Wait for all TTS generations to complete
    const results = await Promise.all(ttsPromises);
    return results;
    
  } catch (error) {
    throw new Error(`Batch TTS generation failed: ${error.message}`);
  }
}

/**
 * Create a document in the audioConversions collection
 * @param {string} text - The text to convert to speech
 * @param {string} languageCode - Language code
 * @param {string} ssmlGender - Voice gender
 * @param {Object} db - Firestore instance
 * @returns {Promise<DocumentReference>} - Reference to the created document
 */
async function createAudioConversionDocument(text, languageCode, ssmlGender, db) {
  try {
    const audioConversionsCollection = collection(db, 'audioConversions');
    
    // Create document with TTS parameters
    const docData = {
      languageCode: languageCode,
      ssmlGender: ssmlGender || 'FEMALE',
      text: text,
      createdAt: new Date(),
      status: 'pending'
    };
    
    const docRef = await addDoc(audioConversionsCollection, docData);
    return docRef;
    
  } catch (error) {
    throw new Error(`Failed to create TTS document: ${error.message}`);
  }
}

/**
 * Wait for audio generation to be processed with retry mechanism
 * @param {DocumentReference} docRef - Reference to the TTS document
 * @param {Object} db - Firestore instance
 * @param {Object} storage - Firebase Storage instance
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} retryDelay - Delay between retries in milliseconds
 * @returns {Promise<string|null>} - The audio URL or null if not found
 */
async function waitForAudioGenerationWithRetries(docRef, db, storage, maxRetries, retryDelay) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      
      const audioUrl = await checkAudioConversionDocument(docRef, db, storage);
      
      if (audioUrl) {
        return audioUrl;
      }
      
      // If no audio URL found and not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
      
    } catch (error) {

      
      // If not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
  

  return null;
}

/**
 * Check if TTS document has been processed by looking for audio file in Storage
 * @param {DocumentReference} docRef - Reference to the TTS document
 * @param {Object} db - Firestore instance
 * @param {Object} storage - Firebase Storage instance
 * @returns {Promise<string|null>} - The audio URL or null if not ready
 */
async function checkAudioConversionDocument(docRef, db, storage) {
  try {
    // First check if document exists
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('TTS document not found');
    }
    
    const data = docSnap.data();
    
    // Check if audioPath is specified in the document
    let audioPath;
    if (data.audioPath) {
      audioPath = data.audioPath;
    } else {
      // Fallback to default path format: audio_conversions/{docId}.mp3
      audioPath = `audio_conversions/${docRef.id}.mp3`;
    }
    
    const audioRef = ref(storage, audioPath);
    
    try {
      // Try to get download URL - this will fail if file doesn't exist
      const downloadURL = await getDownloadURL(audioRef);
      return downloadURL;
    } catch (storageError) {
      // File doesn't exist yet or other storage error
      if (storageError.code === 'storage/object-not-found') {
        return null;
      } else {

        return null;
      }
    }
    
  } catch (error) {
    throw new Error(`Failed to check TTS document: ${error.message}`);
  }
}

/**
 * Wait for TTS using real-time listener (alternative approach)
 * @param {DocumentReference} docRef - Reference to the TTS document
 * @param {Object} db - Firestore instance
 * @param {number} timeoutMs - Maximum time to wait in milliseconds (default: 30000)
 * @returns {Promise<string>} - The audio URL
 */
export async function waitForAudioGenerationRealtime(docRef, db, timeoutMs = 30000) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      reject(new Error('TTS timeout: No audio generated within time limit'));
    }, timeoutMs);
    
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        
        // Check if audio URL is available
        if (data.audioUrl || data.audio_url || data.url) {
          clearTimeout(timeout);
          unsubscribe();
          resolve(data.audioUrl || data.audio_url || data.url);
          return;
        }
        
        // Check for error status
        if (data.status === 'error') {
          clearTimeout(timeout);
          unsubscribe();
          reject(new Error(`TTS failed: ${data.error || 'Unknown error'}`));
          return;
        }
      }
    }, (error) => {
      clearTimeout(timeout);
      reject(new Error(`TTS listener error: ${error.message}`));
    });
  });
}

/**
 * Language code mappings for common languages
 */
export const LanguageCodes = {
  KANNADA: 'kn',
  ENGLISH: 'en',
  HINDI: 'hi',
  TELUGU: 'te',
  TAMIL: 'ta',
  MALAYALAM: 'ml',
  BENGALI: 'bn',
  GUJARATI: 'gu',
  MARATHI: 'mr',
  PUNJABI: 'pa',
  ODIA: 'or',
  ASSAMESE: 'as',
  URDU: 'ur'
};

/**
 * Voice gender options
 */
export const VoiceGenders = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  NEUTRAL: 'NEUTRAL'
};

/**
 * TTS service configuration
 */
export const TTSConfig = {
  // Collection name in Firestore
  collectionName: 'audioConversions',
  
  // Default settings
  defaultLanguageCode: 'kn',
  defaultVoiceGender: 'FEMALE',
  
  // Retry settings
  defaultRetries: 5,
  defaultRetryDelay: 2000, // 2 seconds
  
  // Timeout settings
  defaultTimeout: 30000, // 30 seconds
  
  // Batch processing
  maxBatchSize: 20 // Maximum number of TTS requests to process in parallel
};

/**
 * Update TTS service configuration
 * @param {Object} config - New configuration options
 */
export function updateTTSConfig(config) {
  Object.assign(TTSConfig, config);
}
